import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../context/AuthContext'
import Layout from '../components/Layout'

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado',
  'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho',
  'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana',
  'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
  'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada',
  'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
  'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
  'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
  'West Virginia', 'Wisconsin', 'Wyoming', 'District of Columbia'
]

export default function NewTrust() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    date_established: '',
    state_of_domicile: '',
    jurisdiction: '',
    status: 'active',
    principal_income_allocation_rules: ''
  })

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSubmit = async () => {
    setError(null)

    if (!formData.name.trim()) {
      setError('Trust name is required')
      return
    }
    if (!formData.state_of_domicile) {
      setError('State of domicile is required')
      return
    }

    setLoading(true)

    // Create the trust
    const { data: trustData, error: trustError } = await supabase
      .from('trusts')
      .insert({
        name: formData.name.trim(),
        date_established: formData.date_established || null,
        state_of_domicile: formData.state_of_domicile,
        jurisdiction: formData.jurisdiction || formData.state_of_domicile,
        status: formData.status,
        principal_income_allocation_rules: formData.principal_income_allocation_rules || null,
        created_by: user.id
      })
      .select()
      .single()

    if (trustError) {
      setError(trustError.message)
      setLoading(false)
      return
    }

    // Add the creator as owner in trust_members
    const { error: memberError } = await supabase
      .from('trust_members')
      .insert({
        trust_id: trustData.id,
        user_id: user.id,
        role: 'owner',
        accepted_at: new Date().toISOString()
      })

    if (memberError) {
      setError(memberError.message)
      setLoading(false)
      return
    }

    // Log the action
    await supabase.from('audit_log').insert({
      trust_id: trustData.id,
      user_id: user.id,
      action: 'created_trust',
      table_affected: 'trusts',
      record_id: trustData.id,
      new_value: trustData
    })

    navigate(`/trusts/${trustData.id}`)
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto">

        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/trusts')}
            className="text-gray-400 hover:text-gray-600 text-sm"
          >
            ← Back
          </button>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Create New Trust</h2>
            <p className="text-sm text-gray-500">Enter the trust's basic information</p>
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Trust Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Trust Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. The Smith Family Trust"
            />
          </div>

          {/* Date Established */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date Established
            </label>
            <input
              type="date"
              name="date_established"
              value={formData.date_established}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* State of Domicile */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              State of Domicile <span className="text-red-500">*</span>
            </label>
            <select
              name="state_of_domicile"
              value={formData.state_of_domicile}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a state...</option>
              {US_STATES.map(state => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1">
              Governs applicable state law for principal and income accounting
            </p>
          </div>

          {/* Jurisdiction */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Jurisdiction <span className="text-gray-400">(if different from domicile)</span>
            </label>
            <select
              name="jurisdiction"
              value={formData.jurisdiction}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Same as state of domicile</option>
              {US_STATES.map(state => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="closed">Closed</option>
            </select>
          </div>

          {/* Principal & Income Allocation Rules */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Principal & Income Allocation Rules
            </label>
            <textarea
              name="principal_income_allocation_rules"
              value={formData.principal_income_allocation_rules}
              onChange={handleChange}
              rows={4}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Describe any trust-specific rules that override state law defaults (e.g. 'All rental income allocated to income account per Article IV, Section 2')..."
            />
            <p className="text-xs text-gray-400 mt-1">
              Leave blank to apply standard UPIA rules for the selected state
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating trust...' : 'Create Trust'}
            </button>
            <button
              onClick={() => navigate('/trusts')}
              className="px-6 border border-gray-300 text-gray-600 hover:bg-gray-50 font-medium py-2.5 rounded-lg transition"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </Layout>
  )
}